import posthog from 'posthog-js';
import { v4 as uuidv4 } from 'uuid';
import type {
	AnalyticsContext,
	AnalyticsInitConfig,
	AnalyticsTrackedEvent,
	AnalyticsTrackHandler,
	AnalyticsTrackOptions,
	AnalyticsTrackProperties
} from './AnalyticsSdk.types';
import {
	AnalyticsDevice,
	type AnalyticsEvent,
	type AnalyticsJourney
} from './Events';

const resolveDevice = (): AnalyticsDevice => {
	if (typeof globalThis.matchMedia !== 'function') {
		return AnalyticsDevice.Desktop;
	}

	return globalThis.matchMedia('(max-width: 768px)').matches
		? AnalyticsDevice.Mobile
		: AnalyticsDevice.Desktop;
};

class AnalyticsSdk {
	private context: AnalyticsContext = {};
	private eventSequence = 0;
	private lastEventAt = new Map<AnalyticsEvent, number>();
	private journey: AnalyticsJourney | undefined;
	private journeyId: string | undefined;
	private journeyStartedAt: number | undefined;
	private sessionId = uuidv4();
	private isEnabled = false;
	private isPosthogEnabled = false;
	private trackHandler: AnalyticsTrackHandler | undefined;

	init(config: AnalyticsInitConfig = {}): void {
		const {
			apiHost,
			apiKey,
			enabled = false,
			trackHandler,
			...context
		} = config;

		this.isEnabled = enabled;
		this.context = {
			...this.context,
			...context
		};
		this.trackHandler = trackHandler ?? this.trackHandler;
		this.isPosthogEnabled = false;

		if (enabled && apiKey !== undefined) {
			posthog.init(apiKey, { api_host: apiHost });
			this.isPosthogEnabled = true;
		}
	}

	setContext(context: AnalyticsContext): void {
		this.context = {
			...this.context,
			...context
		};
	}

	clearContext(): void {
		this.context = {};
	}

	startJourney(journey?: AnalyticsJourney): string {
		this.journey = journey;
		this.journeyId = uuidv4();
		this.journeyStartedAt = Date.now();

		return this.journeyId;
	}

	getActiveJourney(): AnalyticsJourney | undefined {
		return this.journey;
	}

	endJourney(): void {
		this.journey = undefined;
		this.journeyId = undefined;
		this.journeyStartedAt = undefined;
	}

	resetSession(): void {
		this.sessionId = uuidv4();
		this.eventSequence = 0;
		this.lastEventAt.clear();
		this.endJourney();
	}

	track(
		name: AnalyticsEvent,
		properties: AnalyticsTrackProperties = {},
		options: AnalyticsTrackOptions = {}
	): AnalyticsTrackedEvent | undefined {
		if (!this.isEnabled) {
			return undefined;
		}

		const now = Date.now();
		this.eventSequence += 1;

		const event: AnalyticsTrackedEvent = {
			name,
			properties: {
				...this.context,
				...properties,
				date_time: new Date().toISOString(),
				device: resolveDevice(),
				duration_ms: this.resolveDuration(options.durationSince, now),
				event_sequence: this.eventSequence,
				journey: properties.journey ?? this.journey,
				journey_id: this.journeyId,
				session_id: this.sessionId,
				time_since_journey_start_ms: this.journeyStartedAt
					? now - this.journeyStartedAt
					: undefined
			}
		};

		this.lastEventAt.set(name, now);
		this.trackHandler?.(event);

		if (this.isPosthogEnabled) {
			posthog.capture(name, event.properties);
		}

		return event;
	}

	private resolveDuration(
		durationSince: AnalyticsTrackOptions['durationSince'],
		now: number
	): number | undefined {
		if (durationSince === undefined) {
			return undefined;
		}

		const events = Array.isArray(durationSince)
			? durationSince
			: [durationSince];
		let startedAt: number | undefined;

		for (const event of events) {
			const timestamp = this.lastEventAt.get(event);
			if (
				timestamp !== undefined &&
				(startedAt === undefined || timestamp > startedAt)
			) {
				startedAt = timestamp;
			}
		}

		return startedAt === undefined ? undefined : now - startedAt;
	}
}

const analyticsSdk = new AnalyticsSdk();

export { analyticsSdk };
